import { useCallback, useContext, useState } from "react";
import { PublicKey } from "@solana/web3.js";
import { useConnection } from "@solana/wallet-adapter-react";
import { AppConfigContext } from "../context";
import * as dripIdl from "../idl/drip_vault_v2.json";
import { DripVaultDescriptor } from "../constants/dripVaults";
import {
  DripVaultState,
  dripProgramFor,
  fetchDripVaultState,
  fetchSolPrice,
} from "../features/drip/dripFetch";

// Re-exported so existing importers (DripDashboardSection) keep working.
export type { DripVaultState };

/** Global stats for one Drip vault. See fetchDripVaultState for the mapping. */
export const useDripVaultState = (vault: DripVaultDescriptor) => {
  const { connection } = useConnection();
  const { dripProgramAddress } = useContext(AppConfigContext);

  const [vaultState, setVaultState] = useState<DripVaultState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVaultState = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const programId = new PublicKey(dripProgramAddress);
      const program = dripProgramFor(dripIdl, dripProgramAddress, connection);
      const solPrice = await fetchSolPrice();

      setVaultState(
        await fetchDripVaultState(program, connection, vault, programId, solPrice)
      );
    } catch (err) {
      console.error(`Error fetching drip vault state (${vault.id}):`, err);
      setError("Error fetching Drip vault state.");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, dripProgramAddress, vault.id, vault.kind, vault.depositMint]);

  return { vaultState, isLoading, error, fetchVaultState };
};
